import { CollapsibleFilters } from "../common/CollapsibleFilters";
import { useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  Checkbox,
  FormControl,
  FormLabel,
  Input,
  Option,
  Select,
  Stack,
  Typography,
} from "@mui/joy";
import { useAuth } from "../../contexts/AuthContext";
import type { Job } from "../../types";
import { ResponsiveTable } from "../common/ResponsiveTable";

export function useJobProductionViews(allJobs: Job[]) {
  const { user } = useAuth();
  const key = `job-production-views:${user?.organization}:${user?.id}`;
  const empty = {
    search: "",
    assignee: "",
    tag: "",
    from: "",
    to: "",
    stalled: false,
    mode: "board",
  };
  const [filters, setFilters] = useState(empty);
  const [saved, setSaved] = useState<Record<string, typeof empty>>(() => {
    try {
      return JSON.parse(localStorage.getItem(key) || "{}");
    } catch {
      return {};
    }
  });
  useEffect(() => {
    try {
      setSaved(JSON.parse(localStorage.getItem(key) || "{}"));
    } catch {
      setSaved({});
    }
  }, [key]);
  const jobs = useMemo(
    () =>
      allJobs.filter((job) => {
        const record = job as any;
        const owner =
          typeof record.assigned_to === "object"
            ? record.assigned_to?.id
            : record.assigned_to;
        const search = [
          record.job_number,
          record.name,
          record.claim_number,
          record.address_1,
          record.city,
          record.description,
        ]
          .join(" ")
          .toLowerCase();
        return (
          (!filters.search || search.includes(filters.search.toLowerCase())) &&
          (!filters.assignee || String(owner) === filters.assignee) &&
          (!filters.tag ||
            record.tags?.some((tag: any) => String(tag.id) === filters.tag)) &&
          (!filters.from ||
            (record.build_date && record.build_date >= filters.from)) &&
          (!filters.to ||
            (record.build_date &&
              record.build_date.slice(0, 10) <= filters.to)) &&
          (!filters.stalled ||
            Date.now() - new Date(record.updated_at).getTime() >= 14 * 86400000)
        );
      }),
    [allJobs, filters],
  );
  const owners = Array.from(
    new Map(
      allJobs.map((job: any) => {
        const owner = job.assigned_to;
        return [
          typeof owner === "object" ? owner?.id : owner,
          typeof owner === "object"
            ? `${owner?.first_name || ""} ${owner?.last_name || ""}`.trim()
            : job.assigned_to_name || `User ${owner}`,
        ];
      }),
    ).entries(),
  ).filter(([id]) => id);
  const tags = Array.from(
    new Map(
      allJobs
        .flatMap((job: any) => job.tags || [])
        .map((tag: any) => [tag.id, tag.name]),
    ).entries(),
  );
  const toolbar = (
    <CollapsibleFilters
      activeCount={
        [
          filters.search,
          filters.assignee,
          filters.tag,
          filters.from,
          filters.to,
          filters.stalled,
        ].filter(Boolean).length
      }
    >
      <Stack spacing={1.5}>
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: {
              xs: "1fr",
              sm: "repeat(2,1fr)",
              lg: "repeat(5,1fr)",
            },
            gap: 1,
          }}
        >
          <FormControl>
            <FormLabel>Search jobs</FormLabel>
            <Input
              value={filters.search}
              onChange={(e) =>
                setFilters({ ...filters, search: e.target.value })
              }
              placeholder="Address, job number, claim…"
            />
          </FormControl>
          <FormControl>
            <FormLabel>Assignee</FormLabel>
            <Select
              value={filters.assignee}
              onChange={(_, v) => setFilters({ ...filters, assignee: v || "" })}
            >
              <Option value="">All assignees</Option>
              {owners.map(([id, name]) => (
                <Option key={String(id)} value={String(id)}>
                  {String(name)}
                </Option>
              ))}
            </Select>
          </FormControl>
          <FormControl>
            <FormLabel>Tag</FormLabel>
            <Select
              value={filters.tag}
              onChange={(_, v) => setFilters({ ...filters, tag: v || "" })}
            >
              <Option value="">All tags</Option>
              {tags.map(([id, name]) => (
                <Option key={String(id)} value={String(id)}>
                  {String(name)}
                </Option>
              ))}
            </Select>
          </FormControl>
          <FormControl>
            <FormLabel>Build from</FormLabel>
            <Input
              type="date"
              value={filters.from}
              onChange={(e) => setFilters({ ...filters, from: e.target.value })}
            />
          </FormControl>
          <FormControl>
            <FormLabel>Build through</FormLabel>
            <Input
              type="date"
              value={filters.to}
              onChange={(e) => setFilters({ ...filters, to: e.target.value })}
            />
          </FormControl>
        </Box>
        <Box
          sx={{
            display: "flex",
            gap: 1,
            alignItems: "center",
            flexWrap: "wrap",
          }}
        >
          <Checkbox
            label="Unchanged for 14+ days"
            checked={filters.stalled}
            onChange={(e) =>
              setFilters({ ...filters, stalled: e.target.checked })
            }
          />
          <Button
            size="sm"
            variant="outlined"
            onClick={() =>
              setFilters({
                ...filters,
                mode: filters.mode === "board" ? "list" : "board",
              })
            }
          >
            {filters.mode === "board" ? "List view" : "Board view"}
          </Button>
          <Button
            size="sm"
            onClick={() => {
              const name = window.prompt("Name this production view");
              if (name?.trim()) {
                const next = { ...saved, [name.trim()]: filters };
                localStorage.setItem(key, JSON.stringify(next));
                setSaved(next);
              }
            }}
          >
            Save view
          </Button>
          <Select
            value={null}
            placeholder="Open saved view"
            onChange={(_, value) => {
              if (value) setFilters({ ...empty, ...saved[String(value)] });
            }}
          >
            {Object.keys(saved).map((name) => (
              <Option key={name} value={name}>
                {name}
              </Option>
            ))}
          </Select>
          <Button size="sm" variant="plain" onClick={() => setFilters(empty)}>
            Reset
          </Button>
          <Typography level="body-sm">
            {jobs.length} of {allJobs.length} jobs
          </Typography>
        </Box>
      </Stack>
    </CollapsibleFilters>
  );
  return { jobs, toolbar, viewMode: filters.mode };
}
export function ProductionJobList({
  jobs,
  onOpen,
}: {
  jobs: Job[];
  onOpen: (job: Job) => void;
}) {
  return (
    <ResponsiveTable
      pagination={{ pageSize: 20, itemNoun: "jobs" }}
      rows={jobs}
      getRowKey={(job) => job.id}
      columns={[
        {
          key: "job",
          header: "Job",
          mobileVariant: "header",
          render: (job) => (
            <Button variant="plain" onClick={() => onOpen(job)}>
              {job.name
                ? `${job.name} · ${job.job_number}`
                : job.job_number || `Job ${job.id}`}
            </Button>
          ),
        },
        {
          key: "property",
          header: "Property",
          render: (job) => (
            <Typography>
              {job.address_1}, {job.city}
            </Typography>
          ),
        },
        {
          key: "stage",
          header: "Stage",
          render: (job) => (
            <Typography>{(job as any).board_status_name || "—"}</Typography>
          ),
        },
        {
          key: "build",
          header: "Build date",
          render: (job) => (
            <Typography>{job.build_date || "Not scheduled"}</Typography>
          ),
        },
        {
          key: "value",
          header: "Value",
          render: (job) => (
            <Typography>
              {Number(job.value || 0).toLocaleString("en-US", {
                style: "currency",
                currency: "USD",
              })}
            </Typography>
          ),
        },
      ]}
      emptyState={<Typography>No jobs match these filters.</Typography>}
    />
  );
}
